// src/store/slices/authSlice.js

import {
  createSlice,
  createAsyncThunk,
  createSelector
} from "@reduxjs/toolkit";

import AuthService from "../services/AuthService";


// ============================================================
// TOKEN / STORAGE KEYS
// ============================================================

const TOKEN_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  USER_DATA: "user_data"
};

// ============================================================
// SAFE LOCAL STORAGE HELPERS
// ============================================================

const getStorageItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Unable to read localStorage key: ${key}`, error);
    return null;
  }
};

const setStorageItem = (key, value) => {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.warn(`Unable to write localStorage key: ${key}`, error);
  }
};

const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Unable to remove localStorage key: ${key}`, error);
  }
};

// ============================================================
// ERROR HANDLER
// ============================================================

const handleError = (error) => {
  let errorMessage = "An unexpected error occurred. Please try again.";

  if (typeof error === "string") {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === "string") {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === "string") {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = typeof error.error === "string" ? error.error : error.error?.message || errorMessage;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  }

  return errorMessage;
};

// ============================================================
// RESPONSE HELPERS
// ============================================================

const extractUser = (response) => {
  return (
    response?.data?.user ||
    response?.user ||
    response?.data?.data?.user ||
    null
  );
};

// ============================================================
// CLEAR SESSION
// ============================================================



// ============================================================
// ASYNC THUNKS
// ============================================================

// ============================================================
// REGISTER
// ============================================================

export const register = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// LOGIN - ✅ FIXED: AuthService handles token storage
// ============================================================

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue,dispatch }) => {
    try {
      // AuthService.login() automatically stores tokens and user data
      const response = await AuthService.login(email, password);
      
      console.log('🔐 Login successful, tokens stored by AuthService');
      console.log('📦 Response:', JSON.stringify(response, null, 2));


      
      return response;
    } catch (error) {
      console.error('❌ Login error:', error);
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// REFRESH TOKEN
// ============================================================

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.refreshToken();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// FORGOT PASSWORD
// ============================================================

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email, language = "ht" }, { rejectWithValue }) => {
    try {
      const response = await AuthService.forgotPassword(email, language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// RESET PASSWORD
// ============================================================

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword, language = "ht" }, { rejectWithValue }) => {
    try {
      const response = await AuthService.resetPassword(token, newPassword, language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// VERIFY EMAIL
// ============================================================

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const response = await AuthService.verifyEmail(token);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// CHECK EMAIL
// ============================================================

export const checkEmail = createAsyncThunk(
  "auth/checkEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await AuthService.checkEmail(email);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// CHECK PHONE
// ============================================================

export const checkPhone = createAsyncThunk(
  "auth/checkPhone",
  async (phone, { rejectWithValue }) => {
    try {
      const response = await AuthService.checkPhone(phone);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// CHECK AUTH
// ============================================================

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = getStorageItem(TOKEN_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        return { data: { isAuthenticated: false, user: null } };
      }

      try {
        const userData = await AuthService.getUserData();
        if (userData) {
          return { data: { isAuthenticated: true, user: userData } };
        }
        return { data: { isAuthenticated: false, user: null } };
      } catch (error) {
        return rejectWithValue({
          message: "Unable to verify the current session.",
          authenticationFailed: true
        });
      }
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);


const clearStoredSession = () => {
  removeStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
  removeStorageItem(TOKEN_KEYS.REFRESH_TOKEN);
  removeStorageItem(TOKEN_KEYS.USER_DATA);
};


// ============================================================
// LOGOUT
// ============================================================

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Clear all tokens and user data
      clearStoredSession();
      
    
      
      // Call logout API (optional - don't block on failure)
      try {
        await AuthService.logout();
      } catch (error) {
        console.warn('Logout API error (ignored):', error);
      }
      
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      // Even if there's an error, we should still clear local state
      clearStoredSession();
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// CHANGE PASSWORD
// ============================================================

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword, language = "ht" }, { rejectWithValue }) => {
    try {
      const response = await AuthService.changePassword(currentPassword, newPassword, language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// RESEND VERIFICATION
// ============================================================

export const resendVerification = createAsyncThunk(
  "auth/resendVerification",
  async (language = "ht", { rejectWithValue }) => {
    try {
      const response = await AuthService.resendVerification(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// UPDATE LANGUAGE
// ============================================================

export const updateLanguage = createAsyncThunk(
  "auth/updateLanguage",
  async (language, { rejectWithValue }) => {
    try {
      const response = await AuthService.updateLanguage(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// GET USER LANGUAGE
// ============================================================

export const getUserLanguage = createAsyncThunk(
  "auth/getUserLanguage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getUserLanguage();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// INITIAL STATE
// ============================================================

const getInitialUser = () => {
  try {
    const storedUser = getStorageItem(TOKEN_KEYS.USER_DATA);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.warn("Failed to parse stored user:", error);
    return null;
  }
};

const initialAccessToken = getStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
const initialRefreshToken = getStorageItem(TOKEN_KEYS.REFRESH_TOKEN);
const initialUser = getInitialUser();

const initialState = {
  user: initialUser,
  isAuthenticated: Boolean(initialAccessToken && initialUser),
  isGuestMode: false,
  accessToken: initialAccessToken,
  refreshToken: initialRefreshToken,
  loading: false,
  registering: false,
  loggingIn: false,
  loggingOut: false,
  error: null,
  registerError: null,
  loginError: null,
  registerSuccess: false,
  loginSuccess: false,
  isEmailVerified: initialUser?.emailVerified === true,
  isPhoneVerified: initialUser?.phoneVerified === true,
  isProfileComplete: initialUser?.isProfileComplete === true,
  language: initialUser?.language || "ht",
  resetPasswordSuccess: false,
  forgotPasswordSuccess: false,
  verificationEmailSent: false,
  authCheckFailed: false
};

// ============================================================
// SLICE
// ============================================================

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.error = null;
      state.registerError = null;
      state.loginError = null;
      state.registerSuccess = false;
      state.loginSuccess = false;
      state.resetPasswordSuccess = false;
      state.forgotPasswordSuccess = false;
      state.verificationEmailSent = false;
      state.authCheckFailed = false;
    },
    resetRegisterState: (state) => {
      state.registerSuccess = false;
      state.registerError = null;
    },
    resetLoginState: (state) => {
      state.loginSuccess = false;
      state.loginError = null;
    },
    resetPasswordResetState: (state) => {
      state.resetPasswordSuccess = false;
      state.forgotPasswordSuccess = false;
    },
    clearAuthErrors: (state) => {
      state.error = null;
      state.registerError = null;
      state.loginError = null;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
      if (state.user) {
        state.user.language = action.payload;
      }
      try {
        const userData = JSON.parse(getStorageItem(TOKEN_KEYS.USER_DATA) || "{}");
        userData.language = action.payload;
        setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
      } catch (error) {
        console.warn("Failed to save language:", error);
      }
    },
    setEmailVerified: (state, action) => {
      const value = action.payload === true;
      state.isEmailVerified = value;
      if (state.user) {
        state.user.emailVerified = value;
      }
      try {
        const userData = JSON.parse(getStorageItem(TOKEN_KEYS.USER_DATA) || "{}");
        userData.emailVerified = value;
        setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
      } catch (error) {
        console.warn("Failed to save email verification:", error);
      }
    },
    setPhoneVerified: (state, action) => {
      const value = action.payload === true;
      state.isPhoneVerified = value;
      if (state.user) {
        state.user.phoneVerified = value;
      }
      try {
        const userData = JSON.parse(getStorageItem(TOKEN_KEYS.USER_DATA) || "{}");
        userData.phoneVerified = value;
        setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
      } catch (error) {
        console.warn("Failed to save phone verification:", error);
      }
    },
    setProfileComplete: (state, action) => {
      state.isProfileComplete = action.payload === true;
    },
   updateUser: (state, action) => {
  const payload = action.payload || {};
  
  // ✅ Handle providerProfile merging properly
  if (payload.providerProfile) {
    state.user = {
      ...(state.user || {}),
      ...payload,
      // Make sure providerProfile is properly merged, not overwritten
      providerProfile: {
        ...(state.user?.providerProfile || {}),
        ...payload.providerProfile
      }
    };
  } else {
    state.user = { ...(state.user || {}), ...payload };
  }
  
  // Update derived fields
  if (payload?.emailVerified !== undefined) {
    state.isEmailVerified = payload.emailVerified === true;
  }
  if (payload?.phoneVerified !== undefined) {
    state.isPhoneVerified = payload.phoneVerified === true;
  }
  if (payload?.isProfileComplete !== undefined) {
    state.isProfileComplete = payload.isProfileComplete === true;
  }
  if (payload?.language !== undefined) {
    state.language = payload.language;
  }
  
  // Update localStorage
  try {
    const existingUser = JSON.parse(getStorageItem(TOKEN_KEYS.USER_DATA) || "{}");
    const updatedUser = { ...existingUser, ...(state.user || {}) };
    setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(updatedUser));
  } catch (error) {
    console.warn("Failed to save updated user:", error);
  }
},
    setGuestMode: (state) => {
      state.isGuestMode = true;
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isEmailVerified = false;
      state.isPhoneVerified = false;
      state.error = null;
      state.loginError = null;
      clearStoredSession();
    },
    exitGuestMode: (state) => {
      state.isGuestMode = false;
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isEmailVerified = false;
      state.isPhoneVerified = false;
      state.error = null;
      state.loginError = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isGuestMode = false;
      state.accessToken = null;
      state.refreshToken = null;
      state.isEmailVerified = false;
      state.isPhoneVerified = false;
      state.isProfileComplete = false;
      state.authCheckFailed = false;
      clearStoredSession();
    },
    setTokens: (state, action) => {
      const { accessToken, refreshToken } = action.payload || {};
      if (accessToken) {
        state.accessToken = accessToken;
        setStorageItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      }
      if (refreshToken) {
        state.refreshToken = refreshToken;
        setStorageItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      }
    },
    clearTokens: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      removeStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
      removeStorageItem(TOKEN_KEYS.REFRESH_TOKEN);
    },
    restoreSession: (state) => {
      try {
        const accessToken = getStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
        const refreshToken = getStorageItem(TOKEN_KEYS.REFRESH_TOKEN);
        const storedUser = getStorageItem(TOKEN_KEYS.USER_DATA);

        if (accessToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          state.accessToken = accessToken;
          state.refreshToken = refreshToken;
          state.user = parsedUser;
          state.isAuthenticated = true;
          state.isGuestMode = false;
          state.isEmailVerified = parsedUser?.emailVerified === true;
          state.isPhoneVerified = parsedUser?.phoneVerified === true;
          state.isProfileComplete = parsedUser?.isProfileComplete === true;
          state.language = parsedUser?.language || "ht";
          state.authCheckFailed = false;
        }
      } catch (error) {
        console.warn("Failed to restore session:", error);
      }
    }
  },

  // ============================================================
  // EXTRA REDUCERS
  // ============================================================

  extraReducers: (builder) => {
    builder
      // ==================== CHECK AUTH ====================
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.authCheckFailed = false;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.authCheckFailed = false;
        const userData = action.payload?.data?.user || action.payload?.user || null;
        if (userData) {
          state.user = userData;
          state.isAuthenticated = true;
          state.isGuestMode = false;
          state.isEmailVerified = userData.emailVerified === true;
          state.isPhoneVerified = userData.phoneVerified === true;
          state.isProfileComplete = userData.isProfileComplete === true;
          state.language = userData.language || "ht";
          setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
        } else {
          const existingToken = state.accessToken || getStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
          const existingUser = state.user;
          if (existingToken && existingUser) {
            state.isAuthenticated = true;
          } else {
            state.isAuthenticated = false;
            state.user = null;
          }
        }
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.loading = false;
        state.authCheckFailed = true;
        state.error = action.payload?.message || action.payload || "Authentication check failed";
        
        const existingToken = state.accessToken || getStorageItem(TOKEN_KEYS.ACCESS_TOKEN);
        const existingUser = state.user;
        if (existingToken && existingUser) {
          state.isAuthenticated = true;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.isEmailVerified = false;
          state.isPhoneVerified = false;
        }
      })

      // ==================== REGISTER ====================
      .addCase(register.pending, (state) => {
        state.registering = true;
        state.registerError = null;
        state.registerSuccess = false;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.registering = false;
        state.registerSuccess = true;
        state.isGuestMode = false;
        
        const userData = extractUser(action.payload);
        if (userData) {
          state.user = userData;
          state.isAuthenticated = true;
          state.isEmailVerified = userData.emailVerified === true;
          state.isPhoneVerified = userData.phoneVerified === true;
          state.isProfileComplete = userData.isProfileComplete === true;
          state.language = userData.language || "ht";
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.registering = false;
        state.registerError = action.payload || "Registration failed";
        state.registerSuccess = false;
      })

      // ==================== LOGIN ====================
      .addCase(login.pending, (state) => {
        state.loggingIn = true;
        state.loginError = null;
        state.loginSuccess = false;
        state.authCheckFailed = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loggingIn = false;
        state.loginSuccess = true;
        state.isGuestMode = false;
        state.authCheckFailed = false;

        // AuthService already saved tokens and user data to localStorage
        // Just read from localStorage to sync state
        try {
          const userData = JSON.parse(localStorage.getItem(TOKEN_KEYS.USER_DATA) || 'null');
          const accessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
          const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
          
          if (userData) {
            state.user = userData;
            state.isAuthenticated = true;
            state.isEmailVerified = userData.emailVerified === true;
            state.isPhoneVerified = userData.phoneVerified === true;
            state.isProfileComplete = userData.isProfileComplete === true;
            state.language = userData.language || "ht";
          }
          
          if (accessToken) {
            state.accessToken = accessToken;
          }
          if (refreshToken) {
            state.refreshToken = refreshToken;
          }
          
          console.log('✅ Login state synced from localStorage');
        } catch (error) {
          console.error('❌ Failed to sync login state:', error);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loggingIn = false;
        state.loginError = action.payload || "Login failed";
        state.loginSuccess = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isEmailVerified = false;
        state.isPhoneVerified = false;
        clearStoredSession();
      })

      // ==================== REFRESH TOKEN ====================
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state) => {
        state.loading = false;
        // AuthService handles token storage
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to refresh token";
        if (!state.user && !state.accessToken) {
          state.isAuthenticated = false;
        }
      })

      // ==================== FORGOT PASSWORD ====================
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.forgotPasswordSuccess = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.forgotPasswordSuccess = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to send reset link";
        state.forgotPasswordSuccess = false;
      })

      // ==================== RESET PASSWORD ====================
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reset password";
        state.resetPasswordSuccess = false;
      })

      // ==================== VERIFY EMAIL ====================
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.loading = false;
        state.isEmailVerified = true;
        if (state.user) {
          state.user.emailVerified = true;
          setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(state.user));
        }
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to verify email";
      })

      // ==================== CHECK EMAIL ====================
      .addCase(checkEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to check email";
      })

      // ==================== CHECK PHONE ====================
      .addCase(checkPhone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPhone.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkPhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to check phone";
      })

      // ==================== LOGOUT ====================
      .addCase(logout.pending, (state) => {
        state.loggingOut = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loggingOut = false;
        state.user = null;
        state.isAuthenticated = false;
        state.isGuestMode = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.isEmailVerified = false;
        state.isPhoneVerified = false;
        state.isProfileComplete = false;
        state.loginSuccess = false;
        state.registerSuccess = false;
        state.authCheckFailed = false;
        clearStoredSession();
      })
      .addCase(logout.rejected, (state, action) => {
        state.loggingOut = false;
        state.error = action.payload || "Logout failed";
        // Clear session anyway
        state.user = null;
        state.isAuthenticated = false;
        state.isGuestMode = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.isEmailVerified = false;
        state.isPhoneVerified = false;
        state.isProfileComplete = false;
        clearStoredSession();
      })

      // ==================== CHANGE PASSWORD ====================
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to change password";
      })

      // ==================== RESEND VERIFICATION ====================
      .addCase(resendVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verificationEmailSent = false;
      })
      .addCase(resendVerification.fulfilled, (state) => {
        state.loading = false;
        state.verificationEmailSent = true;
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to resend verification";
        state.verificationEmailSent = false;
      })

      // ==================== UPDATE LANGUAGE ====================
      .addCase(updateLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.loading = false;
        const language = action.payload?.data?.language || state.language;
        state.language = language;
        if (state.user) {
          state.user.language = language;
          setStorageItem(TOKEN_KEYS.USER_DATA, JSON.stringify(state.user));
        }
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update language";
      })

      // ==================== GET USER LANGUAGE ====================
      .addCase(getUserLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.language = action.payload?.data?.language || "ht";
      })
      .addCase(getUserLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get user language";
      });
  }
});

// ============================================================
// SELECTORS
// ============================================================

const selectAuthState = (state) => state.auth;

export const selectUser = createSelector([selectAuthState], (auth) => auth.user);
export const selectIsAuthenticated = createSelector([selectAuthState], (auth) => auth.isAuthenticated === true);
export const selectIsGuestMode = createSelector([selectAuthState], (auth) => auth.isGuestMode === true);
export const selectAccessToken = createSelector([selectAuthState], (auth) => auth.accessToken);
export const selectRefreshToken = createSelector([selectAuthState], (auth) => auth.refreshToken);
export const selectUserRole = createSelector([selectUser], (user) => user?.role || "user");
export const selectUserName = createSelector([selectUser], (user) => user?.name || "");
export const selectUserEmail = createSelector([selectUser], (user) => user?.email || "");
export const selectUserPhone = createSelector([selectUser], (user) => user?.phone || "");
export const selectUserLanguage = createSelector([selectAuthState], (auth) => auth.language);
export const selectIsProfileComplete = createSelector([selectAuthState], (auth) => auth.isProfileComplete === true);
export const selectAuthLoading = createSelector([selectAuthState], (auth) => auth.loading);
export const selectRegistering = createSelector([selectAuthState], (auth) => auth.registering);
export const selectLoggingIn = createSelector([selectAuthState], (auth) => auth.loggingIn);
export const selectLoggingOut = createSelector([selectAuthState], (auth) => auth.loggingOut);
export const selectAuthError = createSelector([selectAuthState], (auth) => auth.error);
export const selectRegisterError = createSelector([selectAuthState], (auth) => auth.registerError);
export const selectLoginError = createSelector([selectAuthState], (auth) => auth.loginError);
export const selectRegisterSuccess = createSelector([selectAuthState], (auth) => auth.registerSuccess);
export const selectLoginSuccess = createSelector([selectAuthState], (auth) => auth.loginSuccess);
export const selectResetPasswordSuccess = createSelector([selectAuthState], (auth) => auth.resetPasswordSuccess);
export const selectForgotPasswordSuccess = createSelector([selectAuthState], (auth) => auth.forgotPasswordSuccess);
export const selectVerificationEmailSent = createSelector([selectAuthState], (auth) => auth.verificationEmailSent);
export const selectIsEmailVerified = createSelector([selectAuthState], (auth) => auth.isEmailVerified === true);
export const selectIsPhoneVerified = createSelector([selectAuthState], (auth) => auth.isPhoneVerified === true);
export const selectIsAdmin = createSelector([selectUserRole], (role) => role === "admin" || role === "super_admin");
export const selectIsSuperAdmin = createSelector([selectUserRole], (role) => role === "super_admin");
export const selectIsProvider = createSelector([selectUserRole], (role) => role === "service_provider" || role === "admin" || role === "super_admin");
export const selectIsCustomer = createSelector([selectUserRole], (role) => role === "customer" || role === "user" || role === "client");
export const selectUserInitials = createSelector([selectUserName], (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
});
export const selectUserDisplayName = createSelector([selectUser], (user) => {
  if (!user) return "";
  return user.businessName || user.name || user.email || "";
});
export const selectAuthCheckFailed = createSelector([selectAuthState], (auth) => auth.authCheckFailed === true);

// ============================================================
// EXPORT ACTIONS
// ============================================================

export const {
  resetAuthState,
  resetRegisterState,
  resetLoginState,
  resetPasswordResetState,
  clearAuthErrors,
  setLanguage,
  setEmailVerified,
  setPhoneVerified,
  setProfileComplete,
  updateUser,
  clearUser,
  setTokens,
  clearTokens,
  setGuestMode,
  exitGuestMode,
  restoreSession
} = authSlice.actions;

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default authSlice.reducer;